import React, { MouseEventHandler } from 'react';

import { DetailWidgetInteractionItem, StyleProps } from '../WidgetElement';
import { getIconByName } from '../../../MuiIcon/Icon';
import { defaultIconPosition, defaultIconSize, defaultIconSpacing } from '../../../../util/constants';
import { sendInteractGATrackEventData } from '../../../../util/gAUtil';

const generateButtonStyle = (isHover: boolean, clickable: boolean, style: StyleProps) => {
	const {
		textLabel: {
			backgroundColor: textBackgroundColor,
			color,
			fontWeight,
			hoverBackgroundColor: textHoverBackgroundColor,
			hoverFontWeight,
			hoverColor,
			font,
			fontSize,
		},
		transformSettings: { borderRadiusBottom, borderRadiusLeft, borderRadiusRight, borderRadiusTop },
		iconConfig: { position } = {},
		backgroundSettings: { backgroundColor, hoverBackgroundColor },
	} = style;

	// !! Should change to ONLY use backgroundSettings's props after KK-1411 released a few months
	const BGColor = backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== '#00000000' ? backgroundColor : textBackgroundColor;
	const hoverBGColor =
		hoverBackgroundColor && hoverBackgroundColor !== 'transparent' && hoverBackgroundColor !== '#00000000'
			? hoverBackgroundColor
			: textHoverBackgroundColor;

	return {
		display: 'flex',
		flexDirection: position === 'top' || position === 'bottom' ? 'column' : ('row' as any),
		justifyContent: 'center',
		alignItems: 'center',
		textAlign: 'center' as 'center',
		width: '100%',
		height: '100%',
		backgroundColor: isHover ? hoverBGColor : BGColor,
		color: isHover ? hoverColor : color,
		fontFamily: `${font}, Arial, sans-serif`,
		fontSize: `${fontSize}px`,
		fontWeight: isHover ? hoverFontWeight : fontWeight,
		cursor: clickable ? 'pointer' : 'null',
		borderTopRightRadius: `${borderRadiusTop}px`,
		borderBottomRightRadius: `${borderRadiusRight}px`,
		borderBottomLeftRadius: `${borderRadiusBottom}px`,
		borderTopLeftRadius: `${borderRadiusLeft}px`,
	};
};

const generateIconContainerStyle = ({ position, spacing }: { position: string; spacing: string }) => {
	return {
		paddingBottom: position === 'top' ? `${spacing}px` : 0,
		paddingLeft: position === 'right' ? `${spacing}px` : 0,
		paddingRight: position === 'left' ? `${spacing}px` : 0,
		paddingTop: position === 'bottom' ? `${spacing}px` : 0,
		lineHeight: 0,
	};
};

const generateIconStyle = ({ isHover, size, color, hoverColor }: { isHover: boolean; size: string; color: string; hoverColor: string }) => {
	return {
		fontSize: `${size}px`,
		color: !isHover ? color : hoverColor,
	};
};

const Button = ({
	id,
	style,
	data,
	clickable,
	isHover,
	scenarioId,
	sceneId,
	widgetRef,
	onClick,
	handleMouseEnter,
	handleMouseLeave,
}: DetailWidgetInteractionItem) => {
	const {
		textLabel: { text, color, hoverColor },
		coordinate: { x, y },
		dimension: { w = 150, h = 100 },
		rotation,
		iconConfig: { position = defaultIconPosition, size = defaultIconSize, spacing = defaultIconSpacing } = {},
	} = style;

	const centerPoint = {
		x: x + w - w / 2,
		y: y + h - h / 2,
	};

	const {
		settings: { iconKey },
		name,
		type,
	} = data;

	const icon =
		iconKey &&
		getIconByName(iconKey, {
			style: generateIconStyle({ isHover, size, color, hoverColor }),
		});

	const handleWidgetAction = (event: React.TouchEvent | React.MouseEvent) => {
		event.stopPropagation();
		sendInteractGATrackEventData(type, name, scenarioId, sceneId);
		onClick(event);
	};

	return (
		<g
			id={id}
			onTouchStart={handleWidgetAction}
			onClick={handleWidgetAction}
			onMouseEnter={handleMouseEnter as MouseEventHandler<SVGGElement>}
			onMouseLeave={handleMouseLeave as MouseEventHandler<SVGGElement>}
		>
			<foreignObject
				ref={widgetRef}
				x={x}
				y={y}
				width={w}
				height={h}
				style={{ transformOrigin: `${centerPoint.x}px ${centerPoint.y}px`, transform: `rotate(${rotation ?? 0}deg)` }}
			>
				<div style={generateButtonStyle(isHover, clickable, style)}>
					{iconKey && (position === 'top' || position === 'left') && (
						<div style={generateIconContainerStyle({ spacing, position })}>{icon}</div>
					)}

					<span>{text}</span>

					{iconKey && (position === 'bottom' || position === 'right') && (
						<div style={generateIconContainerStyle({ spacing, position })}>{icon}</div>
					)}
				</div>
			</foreignObject>
		</g>
	);
};

export default Button;
